import { AlertCircle, Loader2, Play } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getImageUrl } from '../services/axiosConfig.ts';
import axios from '../utils/axiosHandler';

const Blogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 9;

    useEffect(() => {
        fetchBlogs();
        fetchCategories();
    }, [page, selectedCategory]);

    const fetchBlogs = async () => {
        setLoading(true);
        setError('');

        try {
            const params = {
                page,
                limit,
                sort: 'newest',
                status: 'published',
                populate: 'category,author,tags'
            };

            if (selectedCategory !== 'all') {
                params.category = selectedCategory;
            }

            const response = await axios.get('/api/blog/posts', { params });

            console.log('Blog posts response:', response.data);
            setBlogs(response.data.posts || []);
            setTotalPages(response.data.totalPages || 1);
        } catch (err) {
            console.error('Error fetching blogs:', err);
            setError('Failed to load blog posts. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get('/api/blog/categories');
            setCategories(response.data || []);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getAuthorName = (author) => {
        if (!author) return 'Anonymous';
        if (author.name) return author.name;
        if (author.firstName && author.lastName) {
            return `${author.firstName} ${author.lastName}`;
        }
        if (author.firstName) return author.firstName;
        if (author.username) return author.username;
        return 'Anonymous';
    };

    const handleCategoryClick = (categoryId) => {
        setSelectedCategory(categoryId);
        setPage(1);
    };

    if (loading && blogs.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600">Loading blog posts...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Blog Posts</h2>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    // Get featured post (first blog)
    const featuredPost = blogs.length > 0 ? blogs[0] : null;
    const otherPosts = blogs.slice(1);

    return (
        <div className="min-h-screen bg-white">
            {/* Latest News Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12">
                    Latest News
                </h1>

                {/* Show message if no blogs exist */}
                {!loading && blogs.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-xl text-gray-600 mb-4">No blog posts available yet.</p>
                        <p className="text-gray-500">Check back soon for new content!</p>
                    </div>
                ) : (
                    <>
                        {/* Featured Article + Small Cards Grid */}
                        {featuredPost && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                                {/* Large Featured Card */}
                                <Link
                                    to={`/blog/${featuredPost.slug}`}
                                    className="block group"
                                >
                                    <div className="bg-white rounded-2xl overflow-hidden h-full">
                                        <div className="relative pb-[60%] overflow-hidden">
                                            <img
                                                src={getImageUrl(
                                                    featuredPost.coverImage ||
                                                    (typeof featuredPost.featuredImage === 'string' ? featuredPost.featuredImage : featuredPost.featuredImage?.url) ||
                                                    '/images/placeholder-blog.jpg'
                                                )}
                                                alt={featuredPost.title}
                                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                            />
                                            {featuredPost.podcast?.podcastUrl && (
                                                <div className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg">
                                                    <Play className="w-5 h-5 text-blue-600" fill="currentColor" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Link>

                                {/* Right Column - Small Cards Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-6">
                                    {otherPosts.slice(0, 3).map((post) => (
                                        <Link
                                            key={post._id}
                                            to={`/blog/${post.slug}`}
                                            className="block group"
                                        >
                                            <div className="bg-white rounded-xl overflow-hidden h-full flex flex-col">
                                                <div className="relative pb-[70%] overflow-hidden">
                                                    <img
                                                        src={getImageUrl(
                                                            post.coverImage ||
                                                            (typeof post.featuredImage === 'string' ? post.featuredImage : post.featuredImage?.url) ||
                                                            '/images/placeholder-blog.jpg'
                                                        )}
                                                        alt={post.title}
                                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                                    />
                                                    {post.podcast?.podcastUrl && (
                                                        <div className="absolute top-3 right-3 bg-white rounded-full p-1.5 shadow-lg">
                                                            <Play className="w-4 h-4 text-blue-600" fill="currentColor" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="p-4 flex-1 flex flex-col">
                                                    {post.category && (
                                                        <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
                                                            {post.category.name}
                                                        </span>
                                                    )}
                                                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                                                        {post.title}
                                                    </h3>
                                                    <div className="mt-auto">
                                                        <p className="text-xs text-gray-600">
                                                            {getAuthorName(post.author)} • {formatDate(post.publishedAt || post.createdAt)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Featured Post Info Below Image */}
                        {featuredPost && (
                            <div className="mb-16">
                                {featuredPost.category && (
                                    <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-3 inline-block">
                                        {featuredPost.category.name}
                                    </span>
                                )}
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                    {featuredPost.title}
                                </h2>
                                <p className="text-base text-gray-600">
                                    {getAuthorName(featuredPost.author)} • {formatDate(featuredPost.publishedAt || featuredPost.createdAt)}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Main Content Section with Category Filters */}
            <div className="bg-gray-50 py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
                        Explore Our Articles
                    </h2>

                    {/* Category Filter Buttons */}
                    <div className="flex flex-wrap justify-center gap-3 mb-12">
                        <button
                            onClick={() => handleCategoryClick('all')}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === 'all'
                                ? 'bg-gray-900 text-white shadow-lg'
                                : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                                }`}
                        >
                            All Categories
                        </button>
                        {categories.map((category) => (
                            <button
                                key={category._id}
                                onClick={() => handleCategoryClick(category._id)}
                                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === category._id
                                    ? 'bg-gray-900 text-white shadow-lg'
                                    : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                                    }`}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>

                    {/* Blog Posts Grid */}
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                                {otherPosts.slice(3).map((post) => (
                                    <Link
                                        key={post._id}
                                        to={`/blog/${post.slug}`}
                                        className="block group"
                                    >
                                        <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                                            <div className="relative pb-[60%] overflow-hidden">
                                                <img
                                                    src={getImageUrl(post.coverImage || (typeof post.featuredImage === 'string' ? post.featuredImage : post.featuredImage?.url) || '/images/placeholder-blog.jpg')}
                                                    alt={post.title}
                                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                                />
                                                {post.podcast?.podcastUrl && (
                                                    <div className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg">
                                                        <Play className="w-5 h-5 text-blue-600" fill="currentColor" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-6 flex-1 flex flex-col">
                                                {post.category && (
                                                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-3">
                                                        {post.category.name}
                                                    </span>
                                                )}
                                                <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                                    {post.title}
                                                </h3>
                                                <p className="text-sm text-gray-600 mb-4 line-clamp-2 flex-1">
                                                    {post.excerpt}
                                                </p>
                                                <div className="mt-auto pt-4 border-t border-gray-100">
                                                    <p className="text-sm text-gray-600">
                                                        {getAuthorName(post.author)} • {formatDate(post.publishedAt || post.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            {/* Podcast Section - Show all blogs with podcasts */}
                            {blogs.some(blog => blog.podcast?.podcastUrl) && (
                                <div className="mt-16 pt-16 border-t border-gray-200">
                                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
                                        Listen to Our Podcasts
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                        {blogs.filter(blog => blog.podcast?.podcastUrl).map((post) => (
                                            <div
                                                key={post._id}
                                                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                                            >
                                                <Link to={`/blog/${post.slug}`} className="block">
                                                    <div className="relative pb-[60%] overflow-hidden">
                                                        <img
                                                            src={getImageUrl(post.coverImage || (typeof post.featuredImage === 'string' ? post.featuredImage : post.featuredImage?.url) || '/images/placeholder-blog.jpg')}
                                                            alt={post.title}
                                                            className="absolute inset-0 w-full h-full object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                                            <div className="bg-white rounded-full p-4 shadow-lg">
                                                                <Play className="w-8 h-8 text-blue-600" fill="currentColor" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Link>
                                                <div className="p-6">
                                                    {post.category && (
                                                        <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-3 inline-block">
                                                            {post.category.name}
                                                        </span>
                                                    )}
                                                    <Link to={`/blog/${post.slug}`}>
                                                        <h3 className="text-xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                                                            {post.title}
                                                        </h3>
                                                    </Link>
                                                    <p className="text-sm text-gray-600 mb-4">
                                                        {getAuthorName(post.author)} • {formatDate(post.publishedAt || post.createdAt)}
                                                    </p>
                                                    {post.podcast?.podcastUrl && (
                                                        <audio
                                                            controls
                                                            className="w-full mt-4"
                                                            preload="metadata"
                                                        >
                                                            <source src={post.podcast.podcastUrl} type="audio/mpeg" />
                                                            Your browser does not support the audio element.
                                                        </audio>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex justify-center items-center gap-4 mt-12">
                                    <button
                                        onClick={() => setPage(page - 1)}
                                        disabled={page === 1 || loading}
                                        className="px-6 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium text-gray-700"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-gray-600 text-sm">
                                        Page <span className="font-semibold text-gray-900">{page}</span> of{' '}
                                        <span className="font-semibold text-gray-900">{totalPages}</span>
                                    </span>
                                    <button
                                        onClick={() => setPage(page + 1)}
                                        disabled={page === totalPages || loading}
                                        className="px-6 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium text-gray-700"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Loading Overlay for Pagination */}
            {loading && blogs.length > 0 && (
                <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-xl">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Blogs;

